import { z } from 'zod';
import {
  PermissionGrantSchema,
  PermissionKeySchema,
  SYSTEM_PROJECT_ROLES,
  TenantRoleSchema,
} from './permissions';

/* ----------------------------- roles ----------------------------- */

export const ROLE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
export const RoleKeySchema = z
  .string()
  .regex(ROLE_KEY_PATTERN, 'role key: 2-40 lowercase letters, digits or underscores');

export const isSystemRoleKey = (key: string): boolean =>
  (SYSTEM_PROJECT_ROLES as readonly string[]).includes(key);

export const RoleSchema = z.object({
  id: z.uuid(),
  /** Null = tenant-wide role template copied into new projects. */
  projectId: z.uuid().nullable(),
  key: RoleKeySchema,
  name: z.string().min(1).max(80),
  isSystem: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Role = z.infer<typeof RoleSchema>;

export const CreateRoleSchema = z.strictObject({
  key: RoleKeySchema,
  name: z.string().min(1).max(80),
});
export type CreateRole = z.infer<typeof CreateRoleSchema>;

export const UpdateRoleSchema = z.strictObject({
  name: z.string().min(1).max(80),
});
export type UpdateRole = z.infer<typeof UpdateRoleSchema>;

/* ----------------------------- assignments ----------------------------- */

export const AssignmentSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  roleId: z.uuid(),
  userId: z.uuid().nullable(),
  groupId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
});
export type Assignment = z.infer<typeof AssignmentSchema>;

export const ASSIGNMENT_EXACTLY_ONE_GRANTEE = 'assignment.exactly_one_grantee';

/** Exactly one of `userId` / `groupId` (S-03-03). */
export const CreateAssignmentSchema = z
  .strictObject({
    userId: z.uuid().nullable().optional(),
    groupId: z.uuid().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    const grantees = [value.userId, value.groupId].filter((v) => v != null).length;
    if (grantees !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: `${ASSIGNMENT_EXACTLY_ONE_GRANTEE}: provide exactly one of userId or groupId`,
      });
    }
  });
export type CreateAssignment = z.infer<typeof CreateAssignmentSchema>;

/* ----------------------------- permission schemes ----------------------------- */

export const PermissionSchemeSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable(),
  /** Number of projects using the scheme; present on list/get. */
  projectCount: z.int().min(0).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PermissionScheme = z.infer<typeof PermissionSchemeSchema>;

export const CreatePermissionSchemeSchema = z.strictObject({
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable().default(null),
});
export type CreatePermissionScheme = z.infer<typeof CreatePermissionSchemeSchema>;

export const UpdatePermissionSchemeSchema = z
  .strictObject({
    name: z.string().min(1).max(80),
    description: z.string().max(500).nullable(),
  })
  .partial();
export type UpdatePermissionScheme = z.infer<typeof UpdatePermissionSchemeSchema>;

const grantKey = (g: z.infer<typeof PermissionGrantSchema>): string =>
  `${g.permission}|${g.granteeType}|${g.granteeId ?? ''}`;

/** Grantee types that identify a specific role, user or group and therefore need `granteeId`. */
export const IDENTIFIED_GRANTEE_TYPES = ['project_role', 'tenant_role', 'user', 'group'] as const;

/** `PUT .../grants`: the complete matrix, replaced atomically; duplicates are rejected. */
export const PutGrantsSchema = z
  .array(PermissionGrantSchema)
  .max(500)
  .superRefine((grants, ctx) => {
    const seen = new Set<string>();
    grants.forEach((g, i) => {
      const key = grantKey(g);
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', message: 'duplicate grant', path: [i] });
      }
      seen.add(key);
      const identified = (IDENTIFIED_GRANTEE_TYPES as readonly string[]).includes(g.granteeType);
      if (identified && !g.granteeId) {
        ctx.addIssue({
          code: 'custom',
          message: `granteeId is required for granteeType ${g.granteeType}`,
          path: [i, 'granteeId'],
        });
      }
      if (!identified && g.granteeId !== null) {
        ctx.addIssue({
          code: 'custom',
          message: `granteeId must be null for granteeType ${g.granteeType}`,
          path: [i, 'granteeId'],
        });
      }
    });
  });
export type PutGrants = z.infer<typeof PutGrantsSchema>;

/* ----------------------------- effective permissions ----------------------------- */

export const PERMISSION_SOURCES = [
  'tenant_role',
  'project_role',
  'group',
  'group_grant',
  'user',
  'dynamic',
] as const;
export const PermissionSourceViaSchema = z.enum(PERMISSION_SOURCES);

export const DYNAMIC_GRANTEES = ['reporter', 'assignee', 'project_lead', 'anyone'] as const;

export const PermissionSourceSchema = z.object({
  via: PermissionSourceViaSchema,
  roleId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  tenantRole: TenantRoleSchema.optional(),
  /** Set for `via: 'dynamic'`. */
  grantee: z.enum(DYNAMIC_GRANTEES).optional(),
});
export type PermissionSource = z.infer<typeof PermissionSourceSchema>;

/** `GET /projects/{id}/effective-permissions?userId=` (S-03-05). */
export const EffectivePermissionsSchema = z.object({
  permissions: z.array(PermissionKeySchema),
  sources: z.partialRecord(PermissionKeySchema, z.array(PermissionSourceSchema)),
});
export type EffectivePermissions = z.infer<typeof EffectivePermissionsSchema>;

/* ----------------------------- Bolt 4: access DTOs ----------------------------- */

/** `GET /projects/{id}/assignments`: assignments joined with role and grantee display data. */
export const AssignmentDetailSchema = AssignmentSchema.extend({
  role: z.object({ id: z.uuid(), key: RoleKeySchema, name: z.string() }),
  user: z.object({ id: z.uuid(), displayName: z.string(), email: z.string() }).nullable(),
  group: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export type AssignmentDetail = z.infer<typeof AssignmentDetailSchema>;

/** `GET /permission-schemes/{id}`: the scheme with its full grant matrix. */
export const PermissionSchemeDetailSchema = PermissionSchemeSchema.extend({
  grants: z.array(PermissionGrantSchema),
});
export type PermissionSchemeDetail = z.infer<typeof PermissionSchemeDetailSchema>;

/** `POST /permission-schemes/{id}/copy`. */
export const CopyPermissionSchemeSchema = z.strictObject({
  name: z.string().min(1).max(80),
});
export type CopyPermissionScheme = z.infer<typeof CopyPermissionSchemeSchema>;

/** `POST /projects/{id}/permission-scheme`: switch the project to another scheme. */
export const SwitchPermissionSchemeSchema = z.strictObject({
  permissionSchemeId: z.uuid(),
});
export type SwitchPermissionScheme = z.infer<typeof SwitchPermissionSchemeSchema>;

/** Response of the scheme switch: the project now points at the scheme and its config version moved. */
export const ProjectPermissionSchemeSchema = z.object({
  projectId: z.uuid(),
  permissionSchemeId: z.uuid(),
  configVersion: z.int().min(1),
});
export type ProjectPermissionScheme = z.infer<typeof ProjectPermissionSchemeSchema>;
